import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Globe, Search, ExternalLink, Clock, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const OSINT_BACKEND = process.env.REACT_APP_BACKEND_URL;

const DomainMonitoring = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [expandedResult, setExpandedResult] = useState(null);

  const handleSearch = async () => {
    if (!domain.trim()) {
      toast.error('Please enter a domain to search');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${OSINT_BACKEND}/api/osint/url/urlscanio/${encodeURIComponent(domain)}`, {
        
      });
      
      // Handle the response format from urlscan.io
      if (response.data && Array.isArray(response.data)) {
        setResults(response.data);
        if (response.data.length === 0) {
          toast.info('No scan results found for this domain');
        } else {
          toast.success(`Found ${response.data.length} scan results`);
        }
      } else if (response.data?.results) {
        setResults(response.data.results);
        toast.success(`Found ${response.data.results.length} scan results`);
      } else {
        setResults([]);
        toast.info('No results found');
      }
    } catch (error) {
      console.error('Domain monitoring error:', error);
      toast.error(error.response?.data?.detail || 'Failed to search domain');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getVerdictColor = (verdicts) => {
    if (!verdicts) return 'text-gray-500';
    if (verdicts.overall?.malicious || verdicts.urlscan?.malicious) {
      return 'text-red-600';
    }
    if (verdicts.overall?.hasVerdicts) {
      return 'text-yellow-600';
    }
    return 'text-green-600';
  };

  const getVerdictIcon = (verdicts) => {
    if (!verdicts) return <Shield className="w-5 h-5 text-gray-500" />;
    if (verdicts.overall?.malicious || verdicts.urlscan?.malicious) {
      return <AlertTriangle className="w-5 h-5 text-red-600" />;
    }
    return <CheckCircle className="w-5 h-5 text-green-600" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-3xl font-bold font-heading mb-2" data-testid="domain-monitoring-title">Domain Monitoring</h2>
        <p className="text-muted-foreground">Search for domain scan results and monitor suspicious domains using URLScan.io</p>
      </div>

      {/* Search Input */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Enter domain to search (e.g., example.com)"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full"
                data-testid="domain-search-input"
              />
            </div>
            <Button onClick={handleSearch} disabled={loading || !domain.trim()} data-testid="domain-search-btn">
              <Search className={`w-4 h-4 mr-2 ${loading ? 'animate-pulse' : ''}`} />
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Search for recent scans of a domain using URLScan.io public database
          </p>
        </CardContent>
      </Card>

      {/* Results */}
      {results.length === 0 && !loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Enter a domain to search for scan results</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="domain-results">
          <p className="text-sm text-muted-foreground">
            Found {results.length} scan results for "{domain}"
          </p>
          
          {results.map((result, idx) => (
            <Card 
              key={idx} 
              className={`hover:shadow-lg transition-shadow cursor-pointer ${expandedResult === idx ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setExpandedResult(expandedResult === idx ? null : idx)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {getVerdictIcon(result.verdicts)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-lg truncate">{result.page?.domain || result.task?.domain || 'Unknown'}</h3>
                        {result.verdicts?.overall?.malicious && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">Malicious</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate mt-1">
                        {result.page?.url || result.task?.url || 'N/A'}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(result.task?.time)}
                        </span>
                        {result.page?.ip && (
                          <span>IP: {result.page.ip}</span>
                        )}
                        {result.page?.country && (
                          <span>Country: {result.page.country}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <a
                    href={result.result}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="ml-4 text-primary hover:text-primary/80"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>

                {/* Expanded Details */}
                {expandedResult === idx && (
                  <div className="mt-4 pt-4 border-t space-y-4">
                    {/* Task Information */}
                    {result.task && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Scan Task</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="p-2 bg-muted/50 rounded">
                            <span className="text-muted-foreground">Visibility: </span>
                            <span className="font-medium">{result.task.visibility}</span>
                          </div>
                          <div className="p-2 bg-muted/50 rounded">
                            <span className="text-muted-foreground">Method: </span>
                            <span className="font-medium">{result.task.method}</span>
                          </div>
                          <div className="p-2 bg-muted/50 rounded">
                            <span className="text-muted-foreground">Source: </span>
                            <span className="font-medium">{result.task.source}</span>
                          </div>
                          {result.task.uuid && (
                            <div className="p-2 bg-muted/50 rounded">
                              <span className="text-muted-foreground">UUID: </span>
                              <span className="font-mono text-xs">{result.task.uuid.slice(0, 8)}...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Page Stats */}
                    {result.stats && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Page Statistics</h4>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                          <div className="p-2 bg-muted/50 rounded text-center">
                            <div className="text-lg font-bold text-primary">{result.stats.uniqIPs || 0}</div>
                            <div className="text-xs text-muted-foreground">Unique IPs</div>
                          </div>
                          <div className="p-2 bg-muted/50 rounded text-center">
                            <div className="text-lg font-bold text-primary">{result.stats.uniqCountries || 0}</div>
                            <div className="text-xs text-muted-foreground">Countries</div>
                          </div>
                          <div className="p-2 bg-muted/50 rounded text-center">
                            <div className="text-lg font-bold text-primary">{result.stats.dataLength || 0}</div>
                            <div className="text-xs text-muted-foreground">Data Size</div>
                          </div>
                          <div className="p-2 bg-muted/50 rounded text-center">
                            <div className="text-lg font-bold text-primary">{result.stats.encodedDataLength || 0}</div>
                            <div className="text-xs text-muted-foreground">Encoded Size</div>
                          </div>
                          <div className="p-2 bg-muted/50 rounded text-center">
                            <div className="text-lg font-bold text-primary">{result.stats.requests || 0}</div>
                            <div className="text-xs text-muted-foreground">Requests</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Page Details */}
                    {result.page && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Page Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {result.page.server && (
                            <div className="p-2 bg-muted/50 rounded">
                              <span className="text-muted-foreground">Server: </span>
                              <span className="font-medium">{result.page.server}</span>
                            </div>
                          )}
                          {result.page.mimeType && (
                            <div className="p-2 bg-muted/50 rounded">
                              <span className="text-muted-foreground">MIME Type: </span>
                              <span className="font-medium">{result.page.mimeType}</span>
                            </div>
                          )}
                          {result.page.asn && (
                            <div className="p-2 bg-muted/50 rounded">
                              <span className="text-muted-foreground">ASN: </span>
                              <span className="font-medium">{result.page.asn}</span>
                            </div>
                          )}
                          {result.page.asnname && (
                            <div className="p-2 bg-muted/50 rounded">
                              <span className="text-muted-foreground">ASN Name: </span>
                              <span className="font-medium">{result.page.asnname}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Screenshot Link */}
                    {result.screenshot && (
                      <div className="flex items-center gap-2">
                        <a
                          href={result.screenshot}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Screenshot <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DomainMonitoring;
